import React, { useMemo, useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Info,
    MinusCircle,
    XCircle,
} from "lucide-react";
import { evaluateEventConfig } from "../utils/event-readiness";

const STATUS_META = {
    ok: { label: "Ready", cls: "text-success", Icon: CheckCircle2 },
    warning: { label: "Needs attention", cls: "text-warning", Icon: AlertTriangle },
    blocker: { label: "Action required", cls: "text-error", Icon: XCircle },
    off: { label: "Not used", cls: "opacity-40", Icon: MinusCircle },
};

const SEVERITY_META = {
    blocker: { cls: "text-error", Icon: XCircle },
    warning: { cls: "text-warning", Icon: AlertTriangle },
    info: { cls: "text-info", Icon: Info },
};

const isActionable = (f) =>
    f.severity === "blocker" || f.severity === "warning";

/**
 * Read-only "Event Readiness" checklist shown in the admin event editor.
 * Surfaces, per feature area, how the current configuration will behave on the
 * public side — turning the hidden field interactions into visible guidance.
 *
 * By default it shows only what needs attention (blockers + warnings); the
 * "Details" toggle expands to also show the Ready areas and informational notes.
 */
export default function EventReadinessPanel({ event }) {
    const { areas, summary } = useMemo(
        () => evaluateEventConfig(event),
        [event]
    );
    const [showAll, setShowAll] = useState(false);

    if (!areas.length) return null;

    const visibleAreas = areas
        .map((a) => ({
            ...a,
            visibleFindings: showAll
                ? a.findings
                : a.findings.filter(isActionable),
        }))
        .filter((a) => showAll || a.visibleFindings.length > 0);

    const nothingToShow = !showAll && visibleAreas.length === 0;

    return (
        <div className="w-full border rounded-lg p-4 flex flex-col gap-3 bg-base-200">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-bold text-lg">Event Readiness</p>
                <div className="flex items-center gap-2">
                    {summary.blockers > 0 && (
                        <span className="badge badge-error">
                            {summary.blockers} blocker
                            {summary.blockers > 1 ? "s" : ""}
                        </span>
                    )}
                    {summary.warnings > 0 && (
                        <span className="badge badge-warning">
                            {summary.warnings} warning
                            {summary.warnings > 1 ? "s" : ""}
                        </span>
                    )}
                    {summary.blockers === 0 && summary.warnings === 0 && (
                        <span className="badge badge-success">All good</span>
                    )}
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs gap-1"
                        onClick={() => setShowAll((v) => !v)}
                    >
                        {showAll ? (
                            <>
                                <ChevronUp size={14} /> Hide
                            </>
                        ) : (
                            <>
                                <ChevronDown size={14} /> Details
                            </>
                        )}
                    </button>
                </div>
            </div>

            {nothingToShow ? (
                <div className="flex items-center gap-2 text-sm text-success">
                    <CheckCircle2 size={16} />
                    Nothing needs attention.
                </div>
            ) : (
                <div className="flex flex-col divide-y divide-base-300">
                    {visibleAreas.map((a) => {
                        const meta = STATUS_META[a.status];
                        const StatusIcon = meta.Icon;
                        return (
                            <div
                                key={a.key}
                                className="py-2 flex flex-col gap-1.5"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold">
                                        {a.label}
                                    </span>
                                    <span
                                        className={`inline-flex items-center gap-1 text-sm whitespace-nowrap ${meta.cls}`}
                                    >
                                        <StatusIcon size={16} />
                                        {meta.label}
                                    </span>
                                </div>
                                {a.visibleFindings.map((fd, i) => {
                                    const sm =
                                        SEVERITY_META[fd.severity] ??
                                        SEVERITY_META.info;
                                    const FindingIcon = sm.Icon;
                                    return (
                                        <div
                                            key={i}
                                            className="flex items-start gap-2 pl-1 text-sm"
                                        >
                                            <FindingIcon
                                                size={15}
                                                className={`${sm.cls} mt-0.5 shrink-0`}
                                            />
                                            <span>
                                                <span className="font-medium">
                                                    {fd.title}
                                                </span>
                                                <span className="opacity-70">
                                                    {" — "}
                                                    {fd.consequence}
                                                </span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
